import { Bookmark, BookmarkCheck, ListOrdered, Plus } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { PermissionGate } from '@/auth/PermissionGate';
import { ContenidoProtegido, ImagenProtegida } from '@/components/ContenidoProtegido';
import { usePageEnter } from '@/lib/motion';
import { cn } from '@/lib/utils';

import { Cargando, ErrorCarga } from '../curso/componentes';

import { useEnarmPlus, useMarcador, type EnarmPlus } from './queries';

/** Manual ENARM Plus (skill §10): libro por capítulos con índice y marcador de lectura. */
export function EnarmPlusPage() {
  return (
    <PermissionGate permiso="manual.enarm_plus.access" modulo="Manual ENARM Plus">
      <Contenido />
    </PermissionGate>
  );
}

function Contenido() {
  const { data, error, isPending, refetch } = useEnarmPlus();
  if (isPending) return <Cargando alto="h-96" />;
  if (error) return <ErrorCarga error={error} />;
  return <Libro data={data} onExpirada={() => refetch()} />;
}

const ancla = (id: number) => `bloque-${String(id)}`;

function Libro({ data, onExpirada }: { data: EnarmPlus; onExpirada: () => unknown }) {
  const [marcador, setMarcador] = useState(data.bookmark);
  const [indice, setIndice] = useState(false);
  const guardar = useMarcador();
  const root = useRef<HTMLDivElement>(null);
  usePageEnter(root, []);

  const ir = (id: number) => {
    setIndice(false);
    document.getElementById(ancla(id))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const marcar = (id: number) => {
    setMarcador(id);
    guardar.mutate(id, { onSuccess: () => toast.success('Guardamos tu marcador.') });
  };

  const Indice = (
    <nav aria-label="Índice del manual" className="space-y-3 text-sm">
      {data.chapters.map((c) => (
        <div key={c.id}>
          <p className="font-extrabold text-azul-texto uppercase">{c.title}</p>
          <ul className="mt-1 space-y-0.5">
            {c.blocks
              .filter((b) => b.type === 'titulo' || b.type === 'subtitulo')
              .map((b) => (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => ir(b.id)}
                    className={cn(
                      'w-full rounded px-2 py-1 text-left hover:bg-white/10',
                      b.type === 'subtitulo' && 'pl-5 text-texto-2',
                    )}
                  >
                    {b.title ?? b.subtitle}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    <div ref={root} className="mx-auto max-w-screen-xl space-y-5">
      <header
        className={cn('flex flex-wrap items-center justify-end gap-3', !marcador && 'lg:hidden')}
      >
        <div className="flex gap-2">
          {marcador && (
            <button type="button" className="btn-cs btn-primario" onClick={() => ir(marcador)}>
              <BookmarkCheck size={18} /> Ir a mi marcador
            </button>
          )}
          <button
            type="button"
            className="btn-cs btn-contorno lg:hidden"
            onClick={() => setIndice((v) => !v)}
            aria-expanded={indice}
          >
            <ListOrdered size={18} /> Índice
          </button>
        </div>
      </header>
      <div className="grid gap-6 lg:grid-cols-[17rem_minmax(0,1fr)]">
        {/* Tarjeta «Contenido Temático» del legacy: cabecera con el degradado de marca. */}
        <aside
          className={cn(
            'panel-vidrio max-h-[75vh] flex-col overflow-hidden p-0 lg:sticky lg:top-24 lg:flex',
            indice ? 'flex' : 'hidden',
          )}
        >
          <h2 className="bg-marca px-4 py-2 text-center font-extrabold text-white">
            Contenido Temático
          </h2>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">{Indice}</div>
        </aside>
        <div className="min-w-0 space-y-4">
          {/* Portada del legacy: rótulo verde espaciado sobre marino + logo de Consalud. */}
          <div className="rounded-[var(--radius-card)] bg-[#121F3D] px-4 py-6 text-center">
            <h1 className="text-2xl font-extrabold tracking-[0.2em] text-verde-500 uppercase sm:text-3xl">
              Manual ENARM Plus <Plus className="inline align-[-4px] text-white" size={28} />
            </h1>
            <img
              src="/img/consalud-horizontal-blanco.png"
              alt="Consalud"
              draggable={false}
              className="mx-auto mt-4 h-auto w-44"
            />
          </div>
          <ContenidoProtegido
            contenido={['enarm_plus', 'manual']}
            className="rounded-[var(--radius-card)]"
          >
            <article className="superficie-lectura marca-agua space-y-4 p-5 sm:p-8">
              {data.chapters.length === 0 && (
                <p className="text-texto-2">El manual aún no tiene capítulos.</p>
              )}
              {data.chapters.map((c) => (
                <section key={c.id} className="space-y-4">
                  <h2 className="rounded-lg bg-[#0F3973] px-4 py-2 text-center text-lg font-extrabold text-white uppercase">
                    {c.title}
                  </h2>
                  {c.blocks.map((b) => (
                    <div key={b.id} id={ancla(b.id)} className="group relative scroll-mt-28">
                      {b.type === 'titulo' && (
                        <h3 className="text-xl font-extrabold text-azul-texto">{b.title}</h3>
                      )}
                      {b.type === 'subtitulo' && (
                        <h4 className="text-lg font-bold text-verde-texto">{b.subtitle}</h4>
                      )}
                      {b.type === 'imagen' && b.imageUrl && (
                        <ImagenProtegida
                          src={b.imageUrl}
                          clave={b.id}
                          alt={b.title ?? 'Imagen del manual'}
                          className="mx-auto max-w-xl rounded-xl"
                          onExpirada={onExpirada}
                        />
                      )}
                      {b.type === 'texto' && b.html && (
                        <>
                          <div
                            className="contenido-libro leading-relaxed"
                            // HTML limpio en el servidor (lista blanca de sanitize-html).
                            dangerouslySetInnerHTML={{ __html: b.html }}
                          />
                          <button
                            type="button"
                            onClick={() => marcar(b.id)}
                            aria-label="Guardar marcador aquí"
                            className={cn(
                              'absolute top-0 -right-2 rounded-full p-1.5 transition-opacity sm:-right-6',
                              marcador === b.id
                                ? 'text-amarillo-texto opacity-100'
                                : 'text-texto-3 opacity-0 group-hover:opacity-100 focus:opacity-100',
                            )}
                          >
                            {marcador === b.id ? (
                              <BookmarkCheck size={18} />
                            ) : (
                              <Bookmark size={18} />
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </section>
              ))}
            </article>
          </ContenidoProtegido>
        </div>
      </div>
    </div>
  );
}
